import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ArtistLayout from "@/components/layout/ArtistLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Check, 
  Upload, 
  Image, 
  Video, 
  X,
  Loader2,
  Camera,
  Wine,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ArtistSubmitStep2 = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  
  const [mediaData, setMediaData] = useState({
    merchandise_url: [],
    proof_of_play_url: [],
    bottle_console_url: [],
    branding_stories_post_url: [],
    instagram_analytics_url: null,
    instagram_analytics_impressions: 0,
    track_play_count: 0
  });

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        const response = await axios.get(`${API}/artist/submissions/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmission(response.data);
        setMediaData({
          merchandise_url: Array.isArray(response.data.merchandise_url) ? response.data.merchandise_url : response.data.merchandise_url ? [response.data.merchandise_url] : [],
          proof_of_play_url: Array.isArray(response.data.proof_of_play_url) ? response.data.proof_of_play_url : response.data.proof_of_play_url ? [response.data.proof_of_play_url] : [],
          bottle_console_url: Array.isArray(response.data.bottle_console_url) ? response.data.bottle_console_url : response.data.bottle_console_url ? [response.data.bottle_console_url] : [],
          branding_stories_post_url: Array.isArray(response.data.branding_stories_post_url) ? response.data.branding_stories_post_url : response.data.branding_stories_post_url ? [response.data.branding_stories_post_url] : [],
          instagram_analytics_url: Array.isArray(response.data.instagram_analytics_url) ? (response.data.instagram_analytics_url[0] || null) : response.data.instagram_analytics_url,
          instagram_analytics_impressions: response.data.instagram_analytics_impressions || 0,
          track_play_count: response.data.track_play_count || 0
        });
      } catch (error) {
        console.error("Failed to fetch submission:", error);
        toast.error("Submission not found");
        navigate("/artist");
      } finally {
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [id, token, navigate]);

  const handleFileUpload = async (e, field, maxSizeMB = 100, allowedTypes = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const currentUrls = mediaData[field] || [];
    if (currentUrls.length >= 6) {
      toast.error("Maximum 6 files per field");
      return;
    }

    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size must be under ${maxSizeMB}MB`);
      return;
    }

    // Check file type if specified
    if (allowedTypes) {
      const fileType = file.type.toLowerCase();
      const isAllowed = allowedTypes.some(type => fileType.includes(type));
      if (!isAllowed) {
        toast.error(`Only ${allowedTypes.join(', ').toUpperCase()} files are allowed`);
        return;
      }
    }

    setUploading({ ...uploading, [field]: true });

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setMediaData(prev => ({ ...prev, [field]: [...(prev[field] || []), response.data.url] }));
      toast.success(`File uploaded (${currentUrls.length + 1}/6)`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(getErrorMessage(error, "Failed to upload file"));
    } finally {
      setUploading({ ...uploading, [field]: false });
    }
  };

  const getErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || "").filter(Boolean).join(", ") || fallback;
    return fallback;
  };

  const handleSingleFileUpload = async (e, field, maxSizeMB = 25, allowedTypes = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size must be under ${maxSizeMB}MB`);
      return;
    }

    if (allowedTypes) {
      const fileType = file.type.toLowerCase();
      const isAllowed = allowedTypes.some(type => fileType.includes(type));
      if (!isAllowed) {
        toast.error(`Only ${allowedTypes.join(', ').toUpperCase()} files are allowed`);
        return;
      }
    }

    setUploading({ ...uploading, [field]: true });

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setMediaData(prev => ({ ...prev, [field]: response.data.url }));
      toast.success("File uploaded");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(getErrorMessage(error, "Failed to upload file"));
    } finally {
      setUploading({ ...uploading, [field]: false });
    }
  };

  const handleRemoveFile = (field, index) => {
    setMediaData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      await axios.put(`${API}/artist/submissions/${id}/step2`, mediaData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Submission completed successfully!");
      navigate("/artist");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const FileUploadZone = ({ field, label, description, accept, allowedTypes, maxSizeMB, icon: Icon, currentUrls }) => {
    const inputRef = useRef(null);
    const isUploading = uploading[field];
    const urls = currentUrls || [];
    const canUploadMore = urls.length < 6;

    return (
      <div className="space-y-2">
        <Label className="text-gray-300 uppercase text-xs tracking-wider flex items-center justify-between">
          <span>{label}</span>
          <span className="text-gray-500 normal-case">{urls.length}/6 files</span>
        </Label>
        {description && (
          <p className="text-gray-500 text-xs mb-2">{description}</p>
        )}
        
        {/* Uploaded files grid */}
        {urls.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-2">
            {urls.map((url, idx) => {
              const isVideo = url.includes(".mp4") || url.includes("/video/");
              return (
                <div key={idx} className="relative group border border-gray-700 bg-gray-800 rounded overflow-hidden">
                  {isVideo ? (
                    <video src={url} className="w-full h-24 object-cover" />
                  ) : (
                    <img src={url} alt="" className="w-full h-24 object-cover" loading="lazy" />
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#00B4E5] text-xs hover:underline">View</a>
                    <button
                      onClick={() => handleRemoveFile(field, idx)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="absolute top-1 right-1 bg-green-500/80 rounded-full p-0.5">
                    <Check size={10} className="text-white" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload zone (shown if under 6) */}
        {canUploadMore && (
          <div
            className={`upload-zone ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={(e) => handleFileUpload(e, field, maxSizeMB, allowedTypes)}
              className="hidden"
              data-testid={`upload-${field}`}
            />
            {isUploading ? (
              <Loader2 size={32} className="mx-auto text-[#00B4E5] animate-spin" />
            ) : (
              <Icon size={32} className="mx-auto text-gray-600 mb-2" />
            )}
            <p className="text-gray-400 text-sm">
              {isUploading ? "Uploading..." : urls.length === 0 ? "Click to upload (up to 6 files)" : "Click to add more files"}
            </p>
            <p className="text-gray-600 text-xs mt-1">Max {maxSizeMB}MB per file • {accept.replace(/\./g, '').replace(/,/g, ', ').toUpperCase()}</p>
          </div>
        )}
      </div>
    );
  };

  const SingleFileUploadZone = ({ field, label, description, accept, allowedTypes, maxSizeMB, icon: Icon, currentUrl }) => {
    const inputRef = useRef(null);
    const isUploading = uploading[field];

    return (
      <div className="space-y-2">
        <Label className="text-gray-300 uppercase text-xs tracking-wider flex items-center justify-between">
          <span>{label}</span>
          {currentUrl && <span className="text-green-400 normal-case flex items-center gap-1"><Check size={12} /> Uploaded</span>}
        </Label>
        {description && (
          <p className="text-gray-500 text-xs mb-2">{description}</p>
        )}

        {currentUrl ? (
          <div className="relative group border border-gray-700 bg-gray-800 rounded overflow-hidden w-40">
            <img src={currentUrl} alt="" className="w-full h-24 object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
              <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="text-[#00B4E5] text-xs hover:underline">View</a>
              <button
                onClick={() => setMediaData(prev => ({ ...prev, [field]: null }))}
                className="text-red-400 hover:text-red-300"
                data-testid={`remove-${field}`}
              >
                <X size={18} />
              </button>
            </div>
            <div className="absolute top-1 right-1 bg-green-500/80 rounded-full p-0.5">
              <Check size={10} className="text-white" />
            </div>
          </div>
        ) : (
          <div
            className={`upload-zone ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={(e) => handleSingleFileUpload(e, field, maxSizeMB, allowedTypes)}
              className="hidden"
              data-testid={`upload-${field}`}
            />
            {isUploading ? (
              <Loader2 size={32} className="mx-auto text-[#00B4E5] animate-spin" />
            ) : (
              <Icon size={32} className="mx-auto text-gray-600 mb-2" />
            )}
            <p className="text-gray-400 text-sm">
              {isUploading ? "Uploading..." : "Click to upload"}
            </p>
            <p className="text-gray-600 text-xs mt-1">Max {maxSizeMB}MB • {accept.replace(/\./g, '').replace(/,/g, ', ').toUpperCase()}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <ArtistLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading...</p>
        </div>
      </ArtistLayout>
    );
  }

  return (
    <ArtistLayout>
      <div className="max-w-3xl mx-auto animate-fade-in" data-testid="artist-submit-step2">
        {/* Stepper */}
        <div className="wizard-stepper mb-8">
          <div className="wizard-step completed">
            <div className="wizard-step-number"><Check size={18} /></div>
            <span className="wizard-step-label hidden sm:inline text-green-400">Artist Details</span>
          </div>
          <div className="w-12 h-0.5 bg-green-500 mx-2" />
          <div className="wizard-step active">
            <div className="wizard-step-number">2</div>
            <span className="wizard-step-label hidden sm:inline">Media Upload</span>
          </div>
        </div>

        {/* Summary of Step 1 */}
        <div className="bg-gray-800/50 border border-gray-700 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-gray-400">Artist:</span>{" "}
              <span className="text-white">{submission?.artist_name}</span>
              <span className="text-gray-600 mx-2">•</span>
              <span className="text-gray-400">Venue:</span>{" "}
              <span className="text-white">{submission?.venue}</span>
              <span className="text-gray-600 mx-2">•</span>
              <span className="text-gray-400">Date:</span>{" "}
              <span className="text-white">{submission?.date}</span>
            </div>
            <Link 
              to={`/artist/submission/${id}/edit`}
              className="text-[#00B4E5] text-sm hover:underline"
            >
              Edit
            </Link>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-gray-900 border border-gray-800 p-8">
          <h1 className="text-3xl font-heading text-white mb-2">MEDIA UPLOAD</h1>
          <p className="text-gray-400 mb-8">Upload your campaign media files</p>

          <div className="space-y-6">
            {/* 1. Merchandise */}
            <FileUploadZone
              field="merchandise_url"
              label="Merchandise"
              description="Picture/video with wearing merchandise"
              accept=".jpeg,.jpg,.mp4"
              allowedTypes={['jpeg', 'jpg', 'mp4']}
              maxSizeMB={100}
              icon={Image}
              currentUrls={mediaData.merchandise_url}
            />

            {/* 2. Proof of Play */}
            <FileUploadZone
              field="proof_of_play_url"
              label="Proof of Play"
              description="Picture/video of Rekord Box of track played"
              accept=".jpeg,.jpg,.mp4"
              allowedTypes={['jpeg', 'jpg', 'mp4']}
              maxSizeMB={100}
              icon={Video}
              currentUrls={mediaData.proof_of_play_url}
            />

            {/* 3. Track Play Count */}
            <div className="space-y-2">
              <Label className="text-gray-300 uppercase text-xs tracking-wider">
                How many times was the track played?
              </Label>
              <Select 
                value={String(mediaData.track_play_count)} 
                onValueChange={(v) => setMediaData({ ...mediaData, track_play_count: parseInt(v) })}
              >
                <SelectTrigger className="h-14 bg-black border-gray-700 text-white" data-testid="track-play-count">
                  <SelectValue placeholder="Select count" />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(21)].map((_, i) => (
                    <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 4. Bottle next to Console */}
            <FileUploadZone
              field="bottle_console_url"
              label="Bottle next to Console"
              description="Picture/video of bottle next to the console"
              accept=".jpeg,.jpg,.mp4"
              allowedTypes={['jpeg', 'jpg', 'mp4']}
              maxSizeMB={100}
              icon={Wine}
              currentUrls={mediaData.bottle_console_url}
            />

            {/* 5. Branding - Stories/Post */}
            <FileUploadZone
              field="branding_stories_post_url"
              label="Branding - Stories / Post"
              description="Instagram story/post screenshot"
              accept=".jpeg,.jpg"
              allowedTypes={['jpeg', 'jpg']}
              maxSizeMB={25}
              icon={Camera}
              currentUrls={mediaData.branding_stories_post_url}
            />

            {/* 6. Instagram Analytics */}
            <div className="space-y-4">
              <SingleFileUploadZone
                field="instagram_analytics_url"
                label="Instagram Analytics Screenshot"
                description="Screenshot of reach/impressions/views from your campaign post or story"
                accept=".jpeg,.jpg,.png"
                allowedTypes={['jpeg', 'jpg', 'png']}
                maxSizeMB={25}
                icon={BarChart3}
                currentUrl={mediaData.instagram_analytics_url}
              />
              <div className="space-y-2">
                <Label className="text-gray-300 uppercase text-xs tracking-wider">
                  Total Impressions Generated
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={mediaData.instagram_analytics_impressions || ""}
                  onChange={(e) => setMediaData({ ...mediaData, instagram_analytics_impressions: parseInt(e.target.value) || 0 })}
                  placeholder="Enter total impressions (e.g., 15000)"
                  className="h-14 bg-black border-gray-700 text-white placeholder:text-gray-500"
                  data-testid="instagram-impressions-input"
                />
                <p className="text-xs text-gray-500">Enter the total reach/impressions from your Instagram analytics</p>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-6 flex flex-col sm:flex-row gap-4">
              <Link to={`/artist/submission/${id}/edit`} className="flex-1">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 border-gray-700 text-white hover:bg-gray-800"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Back
                </Button>
              </Link>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 h-14 kf-btn-primary"
                data-testid="submit-campaign-button"
              >
                {submitting ? (
                  <>
                    <Loader2 size={20} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check size={20} className="mr-2" />
                    Submit Campaign
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ArtistLayout>
  );
};

export default ArtistSubmitStep2;
